import {
  MessageCircle, Bot, PackageSearch, FileCheck2, Upload, Calculator,
  UserRound, Clock, Mic, Globe, Sparkles, ArrowRight,
} from "lucide-react";

const WA_NUMBER = "37256011307";
const WA_DISPLAY = "+372 5601 1307";
const WA_LINK = `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent("Hi Europacific, I'd like to track a shipment")}`;
const CHATGPT_LINK = "https://chatgpt.com";

const Bullet = ({ icon: Icon, children }) => (
  <li className="flex items-start gap-2.5">
    <Icon className="w-4 h-4 mt-0.5 flex-shrink-0 opacity-90" />
    <span className="text-[13.5px] leading-snug">{children}</span>
  </li>
);

export const RoketPromoTrack = () => {
  return (
    <section className="py-16 bg-gradient-to-br from-[#0b3d6b] to-[#0a2540]" id="track-channels" data-testid="track-channels-section">
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-10">
          <span className="inline-flex items-center gap-2 bg-white/10 text-white/90 px-4 py-1.5 rounded-full text-sm font-semibold">
            <Sparkles className="w-4 h-4" /> Meet Roket — your AI shipping assistant
          </span>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-white mt-4">Track your shipment your way</h2>
          <p className="text-white/70 mt-2 text-lg max-w-2xl mx-auto">
            Ask Roket for live status, ETA and documents — on WhatsApp or right inside ChatGPT.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* WhatsApp card */}
          <div className="bg-white rounded-2xl p-7 shadow-xl flex flex-col" data-testid="wa-track-card">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-[#25D366] flex items-center justify-center">
                <MessageCircle className="w-7 h-7 text-white" />
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">WhatsApp</div>
                <div className="font-extrabold text-xl text-[#0b3d6b] leading-tight">Track on WhatsApp</div>
              </div>
            </div>
            <p className="text-slate-600 mt-4 text-[15px]">
              Message us your <strong>container, B/L, PF or booking number</strong> and Roket replies instantly
              with live status, ETA and documents — with a human on standby.
            </p>
            <ul className="text-slate-700 mt-5 space-y-2.5">
              <Bullet icon={PackageSearch}>Live tracking &amp; ETA on any reference</Bullet>
              <Bullet icon={FileCheck2}>Proof of delivery (POD) on demand</Bullet>
              <Bullet icon={Upload}>Upload documents — photo or PDF</Bullet>
              <Bullet icon={Calculator}>Request a quote in a couple of taps</Bullet>
              <Bullet icon={UserRound}>Reach your account manager directly</Bullet>
            </ul>
            <div className="flex flex-wrap items-center gap-4 mt-5 text-slate-500 text-xs">
              <span className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> 24/7</span>
              <span className="flex items-center gap-1.5"><Mic className="w-3.5 h-3.5" /> Voice notes</span>
              <span className="flex items-center gap-1.5"><Globe className="w-3.5 h-3.5" /> EN·SL·HR·SR·IT·DE</span>
            </div>
            <div className="mt-auto pt-6">
              <a
                href={WA_LINK}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="wa-track-cta"
                className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1fb855] text-white font-extrabold px-6 py-3.5 rounded-xl transition-colors w-full sm:w-auto justify-center"
              >
                <MessageCircle className="w-5 h-5" /> Chat on WhatsApp · {WA_DISPLAY}
              </a>
            </div>
          </div>

          {/* ChatGPT card */}
          <div className="bg-white rounded-2xl p-7 shadow-xl flex flex-col" data-testid="chatgpt-track-card">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-[#0a2540] flex items-center justify-center">
                <Bot className="w-7 h-7 text-white" />
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">ChatGPT App</div>
                <div className="font-extrabold text-xl text-[#0b3d6b] leading-tight">Track in ChatGPT</div>
              </div>
            </div>
            <p className="text-slate-600 mt-4 text-[15px]">
              Use the <strong>Europacific Tracking</strong> app inside ChatGPT — just ask it to look up any
              shipment and get a live status card, in plain language.
            </p>
            <ul className="text-slate-700 mt-5 space-y-2.5">
              <Bullet icon={Sparkles}>Natural-language tracking — “Where is PF-000700-26?”</Bullet>
              <Bullet icon={PackageSearch}>Search by PF, container, MBL/HBL or booking</Bullet>
              <Bullet icon={FileCheck2}>Instant status card: route, vessel, ETA &amp; milestone</Bullet>
              <Bullet icon={Bot}>Logistics data only — no pricing or personal data</Bullet>
            </ul>
            <div className="flex flex-wrap items-center gap-4 mt-5 text-slate-500 text-xs">
              <span className="flex items-center gap-1.5"><Globe className="w-3.5 h-3.5" /> Works anywhere ChatGPT does</span>
            </div>
            <div className="mt-auto pt-6">
              <a
                href={CHATGPT_LINK}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="chatgpt-track-cta"
                className="inline-flex items-center gap-2 bg-[#0a2540] hover:bg-[#0b3d6b] text-white font-extrabold px-6 py-3.5 rounded-xl transition-colors w-full sm:w-auto justify-center"
              >
                Open in ChatGPT <ArrowRight className="w-5 h-5" />
              </a>
              <p className="text-[11px] text-slate-400 mt-2">Search “Europacific Tracking” in the ChatGPT apps directory.</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default RoketPromoTrack;
